import React, { useMemo } from 'react';
import { ExpeditionStatusEnum, FeaturesEnum, LadenFactorExpeditionOutDTO } from '@api/ringil-api.v2';
import RingilDataTable from '@components/ringil3/RingilDataTable/RingilDataTable';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { Link } from 'react-router-dom';
import useNavigation from '@hooks/useNavigation.tsx';
import { sumPriceDTOs } from '@utils/priceUtils.ts';
import PriceTag from '@components/RingilComponents/tags/priceTag/PriceTag.tsx';
import ManipulationUnitPriceTag from '@components/RingilComponents/tags/priceTag/ManipulationUnitPriceTag.tsx';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import SecuredArea from '@components/security/SecuredArea';

interface ComponentProps {
  expeditions?: LadenFactorExpeditionOutDTO[];
}

interface GroupedExpedition {
  carrierId: string;
  carrierName: string;
  expeditions: LadenFactorExpeditionOutDTO[];
}

const ArrangedQuoteTable: React.FC<ComponentProps> = ({ expeditions = [] }) => {
  const { tr } = useTranslationLgs();
  const navigation = useNavigation();
  const pricePerKmFlag = useSecuredFlag('arranged-price-per-km', {
    kind: 'visibility',
    description: 'Price per km in arranged quotes',
    features: [FeaturesEnum.SHOW_PRICE_PER_KM],
  });
  const pricePerHuFlag = useSecuredFlag('arranged-price-per-hu', {
    kind: 'visibility',
    description: 'Price per handling unit in arranged quotes',
    features: [FeaturesEnum.SHOW_PRICE_PER_HU],
  });
  const showPricePerKm = pricePerKmFlag.granted;

  const groupedExpeditions = useMemo(() => {
    const grouped: Record<string, GroupedExpedition> = {};

    expeditions.forEach(expedition => {
      if (!grouped[expedition.carrierId]) {
        grouped[expedition.carrierId] = {
          carrierId: expedition.carrierId,
          carrierName: expedition.carrierName,
          expeditions: [],
        };
      }

      grouped[expedition.carrierId].expeditions.push(expedition);
    });

    Object.values(grouped).forEach(group => {
      group.expeditions.sort((a, b) =>
        a.expeditionApplicationId.localeCompare(b.expeditionApplicationId),
      );
    });

    return Object.values(grouped);
  }, [expeditions]);

  const renderName = (row: GroupedExpedition) => {
    return <div>{row.carrierName}</div>;
  };

  const rendererLinkedExpeditions = (row: GroupedExpedition) => {
    return (
      <div className="flex-flex-col">
        <div className="flex flex-row flex-wrap gap-x-3">
          {row.expeditions.filter(expedition => expedition.expeditionStatus !== ExpeditionStatusEnum.CANCELED)
            .map(expedition => (
              <Link
                key={expedition.expeditionApplicationId}
                to={navigation.createNavigationLink(navigation.urlFunctions.createExpeditionDetailV2(expedition.expeditionId))}
              >
                {expedition.expeditionApplicationId}
              </Link>
          ))}
        </div>
        <div className="flex flex-row flex-wrap gap-x-3">
          {row.expeditions.filter(expedition => expedition.expeditionStatus === ExpeditionStatusEnum.CANCELED)
            .map(expedition => (
              <Link
                key={expedition.expeditionApplicationId}
                to={navigation.createNavigationLink(navigation.urlFunctions.createExpeditionDetailV2(expedition.expeditionId))}
              >
                <span className="line-through text-content-tertiary">{expedition.expeditionApplicationId}</span>
              </Link>
            ))}
        </div>
      </div>
    );
  };

  const renderPrice = (row: GroupedExpedition) => {
    const ladenFactorUnit = tr(`ArrangedQuoteTable.ladenFactorUnits`, `MU`);
    const distance = row.expeditions[0]?.distance;
    const activeExpeditions = row.expeditions.filter(expedition => expedition.expeditionStatus !== ExpeditionStatusEnum.CANCELED);
    const pricedExpeditions = activeExpeditions.filter(expedition => expedition.priceDTO != null);
    const onlyUnpricedExpeditions = activeExpeditions.length > 0 && pricedExpeditions.length === 0;
    const totalMUs = pricedExpeditions.reduce((sum, expedition) => sum + (expedition.manipulationUnits || 0), 0);
    const totalPriceDto = sumPriceDTOs(pricedExpeditions.map(expedition => expedition.priceDTO));
    const totalPricePerKmDto = {
      ...totalPriceDto,
      price: totalPriceDto?.price && distance
        ? totalPriceDto.price / (distance * pricedExpeditions.length)
        : 0,
      companyComputedPrice: totalPriceDto?.companyComputedPrice && distance
        ? totalPriceDto.companyComputedPrice / (distance * pricedExpeditions.length)
        : 0,
    };
    const totalPricePerMuDto = {
      ...totalPriceDto,
      price: totalPriceDto?.price && totalMUs > 0
        ? totalPriceDto.price / totalMUs
        : 0,
      companyComputedPrice: totalPriceDto?.companyComputedPrice && totalMUs > 0
        ? totalPriceDto.companyComputedPrice / totalMUs
        : 0,
    };



    return (
      <div>
        <div className="flex flex-col gap-1">
          {!onlyUnpricedExpeditions && (
            <SecuredArea id="arranged-price-per-hu" instance={row.carrierId} label="Price per handling unit" description="The arranged price per handling unit" visible={pricePerHuFlag}>
              <ManipulationUnitPriceTag
                badgeType={'neutral'}
                badgeVariant={'outlined'}
                count={totalMUs}
                pricePerUnitDto={totalPricePerMuDto}
                unit={ladenFactorUnit}
              />
            </SecuredArea>
          )}

          {totalPriceDto && (
            <PriceTag
              priceDto={totalPriceDto}
              pricePerUnitDto={showPricePerKm ? totalPricePerKmDto : undefined}
              unit={'km'}
            />
          )}
        </div>

      </div>
    );
  };


  return (
    <>
      <RingilDataTable
        data={groupedExpeditions}
        columns={[
          { name: tr('ArrangedQuoteTable.carrier', 'Carrier'), width: '20%' },
          { name: tr('ArrangedQuoteTable.price', 'Price'), width: '30%' },
          { name: tr('ArrangedQuoteTable.linkedExpeditions', 'Linked expeditions'), width: '50%' },
        ]}
        renderers={[renderName, renderPrice, rendererLinkedExpeditions]}
      />
    </>
  );
};

export default ArrangedQuoteTable;
